/**
 * Hand-coded transportation and directions situational phrases
 * (ESL → Japanese L1 kana).
 * License: CC BY-SA 4.0.
 *
 * Merges what authoritative ESL taxonomies treat as two separate categories
 * (Transportation 8/8 sources + Directions 8/8 sources). The phrases cluster
 * tightly in practice (asking how to get to / using a taxi / reading signs).
 */

import type { SituationCard } from '../../situations/types.js';

const transportDirections: SituationCard[] = [
  {
    id: 'transport.where-is-station',
    situationId: 'transport.where-is-station',
    front: 'Excuse me, where is the nearest station?',
    back: 'すみません、一番近い駅はどこですか？',
    category: 'transport-directions',
    cefr: 'A1',
    translations: { ja: 'すみません、いちばんちかいえきはどこですか？' },
    speechAct: 'request',
    formality: 'polite',
    channel: 'in-person',
    tags: ['directions']
  },
  {
    id: 'transport.taxi-please',
    situationId: 'transport.taxi-please',
    front: 'Could you take me to this address, please?',
    back: 'この住所までお願いします。',
    category: 'transport-directions',
    cefr: 'A1',
    translations: { ja: 'このじゅうしょまでおねがいします。' },
    speechAct: 'request',
    formality: 'polite',
    channel: 'in-person',
    tags: ['taxi']
  },
  {
    id: 'transport.platform',
    situationId: 'transport.platform',
    front: 'Which platform does the train to Boston leave from?',
    back: 'ボストン行きの電車は何番ホームですか？',
    category: 'transport-directions',
    cefr: 'A2',
    translations: { ja: 'ボストンゆきのでんしゃはなんばんホームですか？' },
    speechAct: 'request',
    formality: 'polite',
    channel: 'in-person',
    tags: ['train']
  },
  {
    id: 'transport.how-long',
    situationId: 'transport.how-long',
    front: 'How long does it take to get there?',
    back: 'そこまでどのくらいかかりますか？',
    category: 'transport-directions',
    cefr: 'A2',
    translations: { ja: 'そこまでどのくらいかかりますか？' },
    speechAct: 'request',
    formality: 'polite',
    channel: 'in-person'
  },
  {
    id: 'transport.airport-checkin',
    situationId: 'transport.airport-checkin',
    front: "I'd like to check in for my flight, please.",
    back: 'チェックインをお願いします。',
    category: 'transport-directions',
    cefr: 'A2',
    translations: { ja: 'チェックインをおねがいします。' },
    speechAct: 'request',
    formality: 'polite',
    channel: 'in-person',
    tags: ['airport']
  },
  {
    id: 'transport.lost',
    situationId: 'transport.lost',
    front: "I think I'm lost — could you help me find this place?",
    back: '道に迷ってしまったみたいで……ここへの行き方を教えていただけますか？',
    category: 'transport-directions',
    cefr: 'B1',
    translations: { ja: 'みちにまよってしまったみたいで……ここへのいきかたをおしえていただけますか？' },
    speechAct: 'request',
    formality: 'polite',
    channel: 'in-person',
    pragmaticNote: {
      en: '"I think I\'m lost…" hedges before the request — softer than "Where is X?". B1 learners often skip the hedge and go straight to the question.',
      ja: '「I think I\'m lost…」と前置きしてから依頼すると、いきなり質問するより柔らかい印象になります。'
    }
  },
  {
    id: 'transport.how-much-fare',
    situationId: 'transport.how-much-fare',
    front: 'How much is the fare to downtown?',
    back: 'ダウンタウンまでいくらですか？',
    category: 'transport-directions',
    cefr: 'A2',
    translations: { ja: 'ダウンタウンまでいくらですか？' },
    speechAct: 'request',
    formality: 'polite',
    channel: 'in-person',
    tags: ['payment']
  },
  {
    id: 'transport.next-bus',
    situationId: 'transport.next-bus',
    front: "When's the next bus to the airport?",
    back: '次の空港行きのバスは何時ですか？',
    category: 'transport-directions',
    cefr: 'A2',
    translations: { ja: 'つぎのくうこうゆきのバスはなんじですか？' },
    speechAct: 'request',
    formality: 'polite',
    channel: 'in-person',
    tags: ['bus']
  },
  {
    id: 'transport.directions-walk',
    situationId: 'transport.directions-walk',
    front: 'Is it within walking distance, or should I take a cab?',
    back: '歩いていける距離ですか、それともタクシーを使ったほうがいいですか？',
    category: 'transport-directions',
    cefr: 'B1',
    translations: { ja: 'あるいていけるきょりですか、それともタクシーをつかったほうがいいですか？' },
    speechAct: 'request',
    formality: 'polite',
    channel: 'in-person'
  },
  {
    id: 'transport.confirm-stop',
    situationId: 'transport.confirm-stop',
    front: "Sorry, was that the stop for the museum? I think I missed it.",
    back: 'すみません、今のが美術館の停留所でしたか？乗り過ごしてしまったかも。',
    category: 'transport-directions',
    cefr: 'B1',
    translations: { ja: 'すみません、いまのがびじゅつかんのていりゅうじょでしたか？のりすごしてしまったかも。' },
    speechAct: 'repair',
    formality: 'polite',
    channel: 'in-person',
    tags: ['repair']
  },
  {
    id: 'transport.connection-flight',
    situationId: 'transport.connection-flight',
    front: 'My connecting flight has been delayed — could you help me rebook?',
    back: '乗り継ぎ便が遅れていまして、別の便に振り替えていただけますか？',
    category: 'transport-directions',
    cefr: 'B2',
    translations: { ja: 'のりつぎびんがおくれていまして、べつのびんにふりかえていただけますか？' },
    speechAct: 'request',
    formality: 'formal',
    channel: 'in-person',
    tags: ['airport', 'problem']
  }
];

export default transportDirections;
